import { Button, CheckBox, Input, Label, Modal } from '@iwoca/orion';
import { usePatchIntroducerPermissions } from 'lendingApi/lendingApiHooks';
import { useForm } from 'react-hook-form';
import { PERMISSION_LABELS } from '../cells/PermissionsCell.helpers';
import { TIntroducerListItem } from '../UsersTable.types';
import { diffPermissions, TEditUserFormValues } from './EditUserModal.helpers';
import styles from './EditUserModal.module.css';

type TProps = {
  user: TIntroducerListItem;
  isSelf: boolean;
  isOpen: boolean;
  onClose: () => void;
};

export const EditUserModal = ({ user, isSelf, isOpen, onClose }: TProps) => {
  const { mutate, isPending, isError, reset } = usePatchIntroducerPermissions();

  const { register, handleSubmit } = useForm<TEditUserFormValues>({
    defaultValues: { permissions: user.permissions },
  });

  const onSubmit = (values: TEditUserFormValues) => {
    reset();
    const changedPermissions = diffPermissions(
      user.permissions,
      values.permissions,
    );

    if (Object.keys(changedPermissions).length === 0) {
      onClose();
      return;
    }

    mutate(
      {
        introducerId: user.id,
        body: { data: changedPermissions },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit user - ${user.first_name} ${user.last_name}`}
      size="large"
    >
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.field}>
          <Label htmlFor="edit-user-email">Email</Label>
          <Input
            id="edit-user-email"
            value={user.email ?? ''}
            disabled
            readOnly
          />
        </div>
        <div className={styles.field}>
          <Label htmlFor="edit-user-phone">Phone</Label>
          <Input
            id="edit-user-phone"
            value={user.phone_number ?? ''}
            disabled
            readOnly
          />
        </div>
        <fieldset className={styles.permissions}>
          <legend className={styles.permissionsLegend}>
            Account permissions
          </legend>
          <div className={styles.permissionsGrid}>
            {PERMISSION_LABELS.map((permission) => {
              const isManageUsers =
                permission.key === 'can_manage_account_users';
              return (
                <CheckBox
                  key={permission.key}
                  label={permission.label}
                  disabled={isSelf && isManageUsers}
                  {...register(`permissions.${permission.key}`)}
                />
              );
            })}
          </div>
        </fieldset>
        {isError && (
          <p className={styles.error}>
            Couldn’t save changes. Please try again.
          </p>
        )}
        <div className={styles.footer}>
          <Button
            variant="tertiary"
            type="button"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={isPending}>
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
